/**
 * MeshCollision — triangle-mesh collision queries for the .collision.glb produced
 * by splat-transform's writeVoxel({ collisionMesh: ... }) (or the fused LCC2 mesh).
 *
 * Mirrors the subset of VoxelCollision's interface that CharacterController consumes:
 *   - findGround(x, z, maxY?)         — downward ray vs triangle list
 *   - findGroundInvertedY(x, z, minY) — upward ray vs triangle list
 *   - findGroundWorld(x, z, y, step)  — world-space wrapper used in walk mode
 *   - querySphere(p, r)               — sphere vs nearest triangle, returns push-out
 *   - checkPointSolid / checkSphereSolid — coarse approximations (point-on-mesh test)
 *   - getMetadata / collectSolidAABBs — stubs (no octree to walk)
 *
 * Storage: a flat Float32Array of N×9 triangle vertices (a, b, c each xyz),
 * plus a uniform XZ grid for spatial speedup. The grid is sufficient for the
 * environment-collision case (a few thousand to ~50k triangles).
 *
 * All queries are in **file space** (the splat's local space), matching the
 * voxel collision contract — CharacterController handles world↔file transforms.
 */

import type { PushOut, VoxelOctreeMetadata } from './VoxelCollision';

// glTF accessor componentType values
const COMPONENT_BYTE = 5120;
const COMPONENT_UNSIGNED_BYTE = 5121;
const COMPONENT_SHORT = 5122;
const COMPONENT_UNSIGNED_SHORT = 5123;
const COMPONENT_UNSIGNED_INT = 5125;
const COMPONENT_FLOAT = 5126;

interface GlbChunks {
    json: Record<string, unknown>;
    bin: ArrayBuffer;
}

// Hard caps on attacker-controlled GLB inputs. The GLB URL is part of scene
// data which can be authored by anyone who can save a scene, then served to
// every viewer. Without these a 1KB GLB with a crafted accessor count could
// allocate gigabytes and crash the tab.
const MAX_GLB_BYTES = 50 * 1024 * 1024; // 50 MB
// Must accommodate MAX_TRIANGLES: an index accessor holds 3 entries per
// triangle (and unindexed POSITION accessors hold 3 vertices per triangle).
// Memory safety comes from the bin-bounds check in readAccessor — the data
// must actually exist inside the (≤50 MB) BIN chunk before anything is
// allocated — so this is just a backstop against overlapping-stride tricks.
const MAX_ACCESSOR_COUNT = 15_000_000;
// LCC2 collision meshes for room/site scans routinely exceed 100k tris (each
// chunk PLY contributes a few thousand; fused archives reach the low millions).
// The cap is paired with the 50 MB GLB byte cap above — at ~15 bytes per
// triangle in encoded form, 50 MB tops out around 3–4M triangles in practice.
const MAX_TRIANGLES = 5_000_000;
// Grid is up to ~32×32 cells; each triangle is referenced by every cell its
// AABB overlaps. Sized to ~4× MAX_TRIANGLES for typical overlap.
const MAX_GRID_TRIANGLE_REFERENCES = 20_000_000;

/** Parse a binary glTF (.glb) blob into its JSON document + binary chunk. */
function parseGlb(bytes: Uint8Array): GlbChunks {
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const magic = dv.getUint32(0, true);
    if (magic !== 0x46546c67 /* 'glTF' little-endian */) {
        throw new Error('[MeshCollision] Not a binary GLB (bad magic)');
    }
    const version = dv.getUint32(4, true);
    if (version !== 2) throw new Error(`[MeshCollision] Unsupported GLB version: ${version}`);
    const total = dv.getUint32(8, true);
    if (total > bytes.byteLength) throw new Error('[MeshCollision] GLB length header exceeds buffer');

    let cursor = 12;
    let json: Record<string, unknown> | null = null;
    let bin: ArrayBuffer | null = null;
    while (cursor < total) {
        if (cursor + 8 > total) throw new Error('[MeshCollision] GLB chunk header out of bounds');
        const chunkLength = dv.getUint32(cursor, true);
        const chunkType = dv.getUint32(cursor + 4, true);
        const chunkStart = cursor + 8;
        const chunkEnd = chunkStart + chunkLength;
        if (chunkEnd > total) throw new Error('[MeshCollision] GLB chunk length exceeds container');
        if (chunkType === 0x4e4f534a /* 'JSON' */) {
            const text = new TextDecoder('utf-8').decode(bytes.subarray(chunkStart, chunkEnd));
            // Strip __proto__/constructor keys so a malicious GLB can't smuggle them
            // through downstream consumers that walk the parsed object.
            json = JSON.parse(text, (k, v) => (k === '__proto__' || k === 'constructor') ? undefined : v);
        } else if (chunkType === 0x004e4942 /* 'BIN\0' */) {
            // Copy the chunk into a fresh ArrayBuffer (avoids SharedArrayBuffer ambiguity).
            const slice = bytes.subarray(chunkStart, chunkEnd);
            const copy = new ArrayBuffer(slice.byteLength);
            new Uint8Array(copy).set(slice);
            bin = copy;
        }
        cursor = chunkEnd;
    }
    if (!json) throw new Error('[MeshCollision] GLB has no JSON chunk');
    return { json, bin: bin ?? new ArrayBuffer(0) };
}

function glbTooLargeMessage(byteLength: number): string {
    return `[MeshCollision.load] GLB too large: ${byteLength} bytes (max ${MAX_GLB_BYTES})`;
}

function tooManyTrianglesMessage(triangleCount: number): string {
    return `[MeshCollision.load] GLB contains too many triangles: ${triangleCount} (max ${MAX_TRIANGLES})`;
}

async function readGlbBytesWithCap(resp: Response): Promise<Uint8Array> {
    const contentLength = resp.headers.get('content-length');
    if (contentLength !== null) {
        const declaredBytes = Number(contentLength);
        if (Number.isFinite(declaredBytes) && declaredBytes > MAX_GLB_BYTES) {
            throw new Error(glbTooLargeMessage(declaredBytes));
        }
    }

    if (!resp.body) {
        const buf = new Uint8Array(await resp.arrayBuffer());
        if (buf.byteLength > MAX_GLB_BYTES) {
            throw new Error(glbTooLargeMessage(buf.byteLength));
        }
        return buf;
    }

    const reader = resp.body.getReader();
    const chunks: Uint8Array[] = [];
    let totalBytes = 0;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;

        totalBytes += value.byteLength;
        if (totalBytes > MAX_GLB_BYTES) {
            await reader.cancel();
            throw new Error(glbTooLargeMessage(totalBytes));
        }
        chunks.push(value);
    }

    const buf = new Uint8Array(totalBytes);
    let offset = 0;
    for (const chunk of chunks) {
        buf.set(chunk, offset);
        offset += chunk.byteLength;
    }
    return buf;
}

type Mat4Array = number[];

const IDENTITY_MAT4: Mat4Array = [
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
];

function optionalNumberArray(value: unknown, length: number, label: string): number[] | null {
    if (value === undefined) return null;
    if (!Array.isArray(value) || value.length !== length || !value.every((n) => typeof n === 'number' && Number.isFinite(n))) {
        throw new Error(`[MeshCollision] Invalid ${label}`);
    }
    return value.slice();
}

function multiplyMat4(a: Mat4Array, b: Mat4Array): Mat4Array {
    const out = new Array<number>(16);
    for (let col = 0; col < 4; col++) {
        for (let row = 0; row < 4; row++) {
            out[col * 4 + row] =
                a[0 * 4 + row] * b[col * 4 + 0] +
                a[1 * 4 + row] * b[col * 4 + 1] +
                a[2 * 4 + row] * b[col * 4 + 2] +
                a[3 * 4 + row] * b[col * 4 + 3];
        }
    }
    return out;
}

function composeNodeMatrix(node: Record<string, unknown>, nodeIdx: number): Mat4Array {
    const explicitMatrix = optionalNumberArray(node.matrix, 16, `node ${nodeIdx} matrix`);
    if (explicitMatrix) return explicitMatrix;

    const t = optionalNumberArray(node.translation, 3, `node ${nodeIdx} translation`) ?? [0, 0, 0];
    const r = optionalNumberArray(node.rotation, 4, `node ${nodeIdx} rotation`) ?? [0, 0, 0, 1];
    const s = optionalNumberArray(node.scale, 3, `node ${nodeIdx} scale`) ?? [1, 1, 1];

    const qLen = Math.hypot(r[0], r[1], r[2], r[3]);
    const x = qLen > 1e-8 ? r[0] / qLen : 0;
    const y = qLen > 1e-8 ? r[1] / qLen : 0;
    const z = qLen > 1e-8 ? r[2] / qLen : 0;
    const w = qLen > 1e-8 ? r[3] / qLen : 1;

    const xx = x * x, yy = y * y, zz = z * z;
    const xy = x * y, xz = x * z, yz = y * z;
    const wx = w * x, wy = w * y, wz = w * z;

    return [
        (1 - 2 * (yy + zz)) * s[0],
        (2 * (xy + wz)) * s[0],
        (2 * (xz - wy)) * s[0],
        0,
        (2 * (xy - wz)) * s[1],
        (1 - 2 * (xx + zz)) * s[1],
        (2 * (yz + wx)) * s[1],
        0,
        (2 * (xz + wy)) * s[2],
        (2 * (yz - wx)) * s[2],
        (1 - 2 * (xx + yy)) * s[2],
        0,
        t[0],
        t[1],
        t[2],
        1,
    ];
}

function collectMeshNodeTransforms(json: Record<string, unknown>): Map<number, Mat4Array[]> {
    const nodes = Array.isArray(json.nodes) ? json.nodes as Array<Record<string, unknown>> : [];
    if (nodes.length === 0) return new Map();

    const transforms = new Map<number, Mat4Array[]>();
    const childIndices = new Set<number>();
    for (const node of nodes) {
        const children = node.children;
        if (!Array.isArray(children)) continue;
        for (const child of children) {
            if (typeof child !== 'number' || child < 0 || child >= nodes.length) {
                throw new Error(`[MeshCollision] Invalid child node index: ${child}`);
            }
            childIndices.add(child);
        }
    }

    let rootIndices: number[] = [];
    const scenes = Array.isArray(json.scenes) ? json.scenes as Array<Record<string, unknown>> : [];
    if (scenes.length > 0) {
        const sceneIdx = typeof json.scene === 'number' && json.scene >= 0 && json.scene < scenes.length ? json.scene : 0;
        const sceneNodes = scenes[sceneIdx].nodes;
        if (Array.isArray(sceneNodes)) {
            rootIndices = sceneNodes.map((idx) => {
                if (typeof idx !== 'number' || idx < 0 || idx >= nodes.length) {
                    throw new Error(`[MeshCollision] Invalid scene root node index: ${idx}`);
                }
                return idx;
            });
        }
    }
    if (rootIndices.length === 0) {
        rootIndices = nodes.map((_, idx) => idx).filter((idx) => !childIndices.has(idx));
    }
    if (rootIndices.length === 0) {
        rootIndices = nodes.map((_, idx) => idx);
    }

    const visit = (nodeIdx: number, parentWorld: Mat4Array, path: Set<number>) => {
        if (path.has(nodeIdx)) throw new Error(`[MeshCollision] Node cycle detected at ${nodeIdx}`);
        const node = nodes[nodeIdx];
        const nextPath = new Set(path).add(nodeIdx);
        const world = multiplyMat4(parentWorld, composeNodeMatrix(node, nodeIdx));

        if (typeof node.mesh === 'number') {
            const list = transforms.get(node.mesh) ?? [];
            list.push(world);
            transforms.set(node.mesh, list);
        }

        const children = node.children;
        if (Array.isArray(children)) {
            for (const child of children) {
                visit(child as number, world, nextPath);
            }
        }
    };

    for (const rootIdx of rootIndices) {
        visit(rootIdx, IDENTITY_MAT4, new Set<number>());
    }

    return transforms;
}

function componentSize(componentType: number): number {
    switch (componentType) {
        case COMPONENT_BYTE: case COMPONENT_UNSIGNED_BYTE: return 1;
        case COMPONENT_SHORT: case COMPONENT_UNSIGNED_SHORT: return 2;
        case COMPONENT_UNSIGNED_INT: case COMPONENT_FLOAT: return 4;
        default: throw new Error(`[MeshCollision] Unknown componentType ${componentType}`);
    }
}

function typeNumComponents(type: string): number {
    switch (type) {
        case 'SCALAR': return 1;
        case 'VEC2': return 2;
        case 'VEC3': return 3;
        case 'VEC4': return 4;
        default: throw new Error(`[MeshCollision] Unsupported accessor type ${type}`);
    }
}

/**
 * Read an accessor's typed array from a parsed GLB. Handles interleaved
 * bufferViews (byteStride). Returns a typed array view into a fresh contiguous
 * Float32/Uint32 buffer, depending on the accessor's componentType.
 */
function readAccessor(
    accessorIdx: number,
    json: Record<string, unknown>,
    bin: ArrayBuffer,
): { array: Float32Array | Uint32Array | Uint16Array | Uint8Array; numComponents: number } {
    const accessors = json.accessors as Array<Record<string, unknown>>;
    const bufferViews = json.bufferViews as Array<Record<string, unknown>>;
    const acc = accessors[accessorIdx];
    if (!acc) throw new Error(`[MeshCollision] Missing accessor ${accessorIdx}`);

    const bvIdx = acc.bufferView as number;
    const bv = bufferViews[bvIdx];
    if (!bv) throw new Error(`[MeshCollision] Missing bufferView ${bvIdx}`);

    const componentType = acc.componentType as number;
    const count = acc.count as number;
    if (typeof count !== 'number' || !Number.isFinite(count) || count < 0 || count > MAX_ACCESSOR_COUNT) {
        throw new Error(`[MeshCollision] Accessor ${accessorIdx} count out of range: ${count}`);
    }
    const numComponents = typeNumComponents(acc.type as string);
    const elementSize = numComponents * componentSize(componentType);
    const accByteOffset = (acc.byteOffset as number) ?? 0;
    const bvByteOffset = (bv.byteOffset as number) ?? 0;
    const start = bvByteOffset + accByteOffset;
    const stride = (bv.byteStride as number) ?? elementSize;
    if (!Number.isFinite(start) || start < 0 || !Number.isFinite(stride) || stride < elementSize) {
        throw new Error(`[MeshCollision] Accessor ${accessorIdx} has invalid offset/stride`);
    }
    // Verify the data actually exists in the BIN chunk BEFORE allocating the
    // output — this, not MAX_ACCESSOR_COUNT, is what stops a tiny GLB with a
    // crafted count from allocating gigabytes.
    const lastByteNeeded = count > 0 ? start + (count - 1) * stride + elementSize : start;
    if (lastByteNeeded > bin.byteLength) {
        throw new Error(
            `[MeshCollision] Accessor ${accessorIdx} data out of bounds: needs ${lastByteNeeded} bytes, BIN has ${bin.byteLength}`
        );
    }
    const dv = new DataView(bin);

    // Allocate output array of correct type
    let out: Float32Array | Uint32Array | Uint16Array | Uint8Array;
    switch (componentType) {
        case COMPONENT_FLOAT: out = new Float32Array(count * numComponents); break;
        case COMPONENT_UNSIGNED_INT: out = new Uint32Array(count * numComponents); break;
        case COMPONENT_UNSIGNED_SHORT: out = new Uint16Array(count * numComponents); break;
        case COMPONENT_UNSIGNED_BYTE: out = new Uint8Array(count * numComponents); break;
        default:
            throw new Error(`[MeshCollision] componentType ${componentType} not supported for collision data`);
    }

    for (let i = 0; i < count; i++) {
        const elementStart = start + i * stride;
        for (let c = 0; c < numComponents; c++) {
            const off = elementStart + c * componentSize(componentType);
            let v: number;
            switch (componentType) {
                case COMPONENT_FLOAT: v = dv.getFloat32(off, true); break;
                case COMPONENT_UNSIGNED_INT: v = dv.getUint32(off, true); break;
                case COMPONENT_UNSIGNED_SHORT: v = dv.getUint16(off, true); break;
                case COMPONENT_UNSIGNED_BYTE: v = dv.getUint8(off); break;
                default: v = 0;
            }
            out[i * numComponents + c] = v;
        }
    }
    return { array: out, numComponents };
}

interface AABB {
    minX: number; minY: number; minZ: number;
    maxX: number; maxY: number; maxZ: number;
}

export class MeshCollision {
    // Original mesh-local triangles & bounds (kept so setSplatTransform can re-bake
    // when the splat entity's transform changes).
    private readonly rawTris: Float32Array;
    private readonly rawBounds: AABB;

    private tris: Float32Array;     // 9 floats per triangle: ax,ay,az, bx,by,bz, cx,cy,cz (current world-space data)
    private triCount: number;
    private bounds: AABB;

    // Uniform XZ grid: triangle indices bucketed by (x,z) cells.
    private cellSize: number = 0;
    private gridMinX: number = 0;
    private gridMinZ: number = 0;
    private gridDimX: number = 0;
    private gridDimZ: number = 0;
    private cellOffsets: Uint32Array = new Uint32Array(1);  // length = numCells + 1
    private cellTris: Uint32Array = new Uint32Array(0);     // flattened tri indices, indexed via cellOffsets

    // Pending splat transform — applied lazily on the next query so that a
    // continuous gizmo drag (which calls setSplatTransform on every tick) doesn't
    // pay the O(triCount) re-bake + grid-rebuild cost per tick. The drag is free;
    // the rebuild happens once, the next time the player actually probes for
    // collision. `pendingMatrix === undefined` means "no change pending".
    private pendingMatrix: { data: Float32Array | number[] } | null | undefined = undefined;

    constructor(tris: Float32Array, bounds: AABB) {
        if (tris.length % 9 !== 0) {
            throw new Error(`MeshCollision: tris length must be multiple of 9, got ${tris.length}`);
        }
        this.rawTris = tris;
        this.rawBounds = bounds;
        this.tris = tris;
        this.triCount = tris.length / 9;
        this.bounds = bounds;
        this.buildGrid();
    }

    /** Triangle count of the loaded mesh (for diagnostics). */
    get triangleCount(): number {
        return this.triCount;
    }

    /**
     * Mark a new splat transform as pending. The actual triangle re-bake and
     * grid rebuild happen on the next query (see {@link applyPendingTransform})
     * so that a continuous gizmo drag — which fires this on every tick via
     * CharacterController.refreshSplatCollisionTransform — doesn't grind the
     * editor to a halt. Pass null to revert to raw mesh-local data.
     *
     * Pre-baking the world matrix into the triangles decouples queries from
     * splat coordinate conventions (e.g. LCC2's Z-up data with the [90,90,0]
     * viewer rotation correction), which the XZ grid + Y-down ground rays would
     * otherwise misinterpret.
     */
    setSplatTransform(matrix: { data: Float32Array | number[] } | null): void {
        // Snapshot the matrix data so a later mutation by the caller doesn't
        // change what we eventually bake. Most callers pass a live pc.Mat4.data
        // that gets reused across frames, which would otherwise read whatever
        // the entity's transform is at flush time (might be fine, might not).
        if (matrix) {
            const src = matrix.data;
            const snapshot = new Float32Array(16);
            for (let i = 0; i < 16; i++) snapshot[i] = src[i];
            this.pendingMatrix = { data: snapshot };
        } else {
            this.pendingMatrix = null;
        }
    }

    /** Force-apply any pending transform now. Cheap when nothing is pending. */
    flushPendingTransform(): void {
        if (this.pendingMatrix === undefined) return;
        const matrix = this.pendingMatrix;
        this.pendingMatrix = undefined;
        if (!matrix) {
            this.tris = this.rawTris;
            this.bounds = this.rawBounds;
        } else {
            const m = matrix.data;
            const out = new Float32Array(this.rawTris.length);
            for (let i = 0; i < this.rawTris.length; i += 3) {
                const x = this.rawTris[i], y = this.rawTris[i + 1], z = this.rawTris[i + 2];
                out[i]     = m[0] * x + m[4] * y + m[8] * z + m[12];
                out[i + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
                out[i + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
            }
            this.tris = out;
            let bMinX = Infinity, bMinY = Infinity, bMinZ = Infinity;
            let bMaxX = -Infinity, bMaxY = -Infinity, bMaxZ = -Infinity;
            for (let i = 0; i < out.length; i += 3) {
                const x = out[i], y = out[i + 1], z = out[i + 2];
                if (x < bMinX) bMinX = x; if (x > bMaxX) bMaxX = x;
                if (y < bMinY) bMinY = y; if (y > bMaxY) bMaxY = y;
                if (z < bMinZ) bMinZ = z; if (z > bMaxZ) bMaxZ = z;
            }
            this.bounds = {
                minX: bMinX, minY: bMinY, minZ: bMinZ,
                maxX: bMaxX, maxY: bMaxY, maxZ: bMaxZ,
            };
        }
        this.triCount = this.tris.length / 9;
        this.buildGrid();
    }

    private buildGrid(): void {
        const tris = this.tris;
        const bounds = this.bounds;

        // Pick a cell size proportional to scene extent. Aim for ~10 cells per axis,
        // capped to a reasonable absolute size. Triangles in a typical room scene
        // average a few cm, so a 1m cell keeps each cell's triangle list small.
        const extentX = Math.max(0.001, bounds.maxX - bounds.minX);
        const extentZ = Math.max(0.001, bounds.maxZ - bounds.minZ);
        const targetExtent = Math.max(extentX, extentZ);
        this.cellSize = Math.max(0.5, targetExtent / 32);

        this.gridMinX = bounds.minX;
        this.gridMinZ = bounds.minZ;
        this.gridDimX = Math.max(1, Math.ceil(extentX / this.cellSize));
        this.gridDimZ = Math.max(1, Math.ceil(extentZ / this.cellSize));

        // Two-pass grid build: count, allocate, fill.
        const numCells = this.gridDimX * this.gridDimZ;
        const counts = new Uint32Array(numCells);

        const writeCells = (cb: (cellIdx: number, triIdx: number) => void) => {
            for (let i = 0; i < this.triCount; i++) {
                const o = i * 9;
                const ax = tris[o], az = tris[o + 2];
                const bx = tris[o + 3], bz = tris[o + 5];
                const cx = tris[o + 6], cz = tris[o + 8];
                const triMinX = Math.min(ax, bx, cx);
                const triMaxX = Math.max(ax, bx, cx);
                const triMinZ = Math.min(az, bz, cz);
                const triMaxZ = Math.max(az, bz, cz);
                const cellMinX = Math.max(0, Math.floor((triMinX - this.gridMinX) / this.cellSize));
                const cellMaxX = Math.min(this.gridDimX - 1, Math.floor((triMaxX - this.gridMinX) / this.cellSize));
                const cellMinZ = Math.max(0, Math.floor((triMinZ - this.gridMinZ) / this.cellSize));
                const cellMaxZ = Math.min(this.gridDimZ - 1, Math.floor((triMaxZ - this.gridMinZ) / this.cellSize));
                for (let cz_ = cellMinZ; cz_ <= cellMaxZ; cz_++) {
                    for (let cx_ = cellMinX; cx_ <= cellMaxX; cx_++) {
                        cb(cz_ * this.gridDimX + cx_, i);
                    }
                }
            }
        };

        // Pass 1: count
        writeCells((cellIdx) => { counts[cellIdx]++; });

        // Build offsets (prefix sum)
        this.cellOffsets = new Uint32Array(numCells + 1);
        for (let i = 0; i < numCells; i++) {
            this.cellOffsets[i + 1] = this.cellOffsets[i] + counts[i];
        }
        const totalEntries = this.cellOffsets[numCells];
        if (totalEntries > MAX_GRID_TRIANGLE_REFERENCES) {
            throw new Error(
                `MeshCollision: grid expansion too large: ${totalEntries} triangle references ` +
                `(max ${MAX_GRID_TRIANGLE_REFERENCES})`
            );
        }
        this.cellTris = new Uint32Array(totalEntries);

        // Pass 2: fill (reuse counts as cursors)
        counts.fill(0);
        writeCells((cellIdx, triIdx) => {
            const slot = this.cellOffsets[cellIdx] + counts[cellIdx];
            this.cellTris[slot] = triIdx;
            counts[cellIdx]++;
        });
    }

    /**
     * Load MeshCollision from a .collision.glb URL.
     * Extracts all triangle positions from every primitive, with node transforms
     * applied into the GLB's scene space.
     * (writeVoxel emits a single mesh in splat-local space; LCC2 fusion emits the
     *  same, normally with identity node transforms.)
     */
    static async load(glbUrl: string): Promise<MeshCollision> {
        console.log(`[MeshCollision.load] Fetching GLB: ${glbUrl.substring(0, 100)}...`);
        const resp = await fetch(glbUrl);
        if (!resp.ok) throw new Error(`[MeshCollision.load] Failed to fetch GLB: ${resp.status} ${resp.statusText}`);
        const buf = await readGlbBytesWithCap(resp);
        console.log(`[MeshCollision.load] GLB fetched: ${buf.byteLength} bytes`);

        const { json, bin } = parseGlb(buf);
        const meshes = (json.meshes as Array<Record<string, unknown>>) ?? [];
        const meshNodeTransforms = collectMeshNodeTransforms(json);

        const triList: number[] = [];
        let emittedTriangles = 0;
        let bMinX = Infinity, bMinY = Infinity, bMinZ = Infinity;
        let bMaxX = -Infinity, bMaxY = -Infinity, bMaxZ = -Infinity;

        const ensureTriangleBudget = (additionalTriangles: number) => {
            if (!Number.isInteger(additionalTriangles) || additionalTriangles < 0) {
                throw new Error(`[MeshCollision.load] Invalid triangle count: ${additionalTriangles}`);
            }
            const nextCount = emittedTriangles + additionalTriangles;
            if (nextCount > MAX_TRIANGLES) {
                throw new Error(tooManyTrianglesMessage(nextCount));
            }
        };

        const emitMesh = (mesh: Record<string, unknown>, transform: Mat4Array) => {
            const primitives = (mesh.primitives as Array<Record<string, unknown>>) ?? [];
            for (const prim of primitives) {
                const attrs = prim.attributes as Record<string, number>;
                const posIdx = attrs?.POSITION;
                if (posIdx === undefined) continue;
                const positions = readAccessor(posIdx, json, bin);
                if (positions.numComponents !== 3) continue;
                const posArr = positions.array as Float32Array;
                const vertCount = posArr.length / 3;

                let indices: Uint32Array | Uint16Array | Uint8Array | null = null;
                if (prim.indices !== undefined) {
                    const idxRead = readAccessor(prim.indices as number, json, bin);
                    indices = idxRead.array as Uint32Array | Uint16Array | Uint8Array;
                }

                const emitTri = (ai: number, bi: number, ci: number) => {
                    if (ai >= vertCount || bi >= vertCount || ci >= vertCount) {
                        throw new Error('[MeshCollision.load] Triangle index references missing position data');
                    }
                    const a0 = ai * 3, b0 = bi * 3, c0 = ci * 3;
                    const arx = posArr[a0], ary = posArr[a0 + 1], arz = posArr[a0 + 2];
                    const brx = posArr[b0], bry = posArr[b0 + 1], brz = posArr[b0 + 2];
                    const crx = posArr[c0], cry = posArr[c0 + 1], crz = posArr[c0 + 2];
                    const ax = transform[0] * arx + transform[4] * ary + transform[8] * arz + transform[12];
                    const ay = transform[1] * arx + transform[5] * ary + transform[9] * arz + transform[13];
                    const az = transform[2] * arx + transform[6] * ary + transform[10] * arz + transform[14];
                    const bx = transform[0] * brx + transform[4] * bry + transform[8] * brz + transform[12];
                    const by = transform[1] * brx + transform[5] * bry + transform[9] * brz + transform[13];
                    const bz = transform[2] * brx + transform[6] * bry + transform[10] * brz + transform[14];
                    const cx = transform[0] * crx + transform[4] * cry + transform[8] * crz + transform[12];
                    const cy = transform[1] * crx + transform[5] * cry + transform[9] * crz + transform[13];
                    const cz = transform[2] * crx + transform[6] * cry + transform[10] * crz + transform[14];
                    triList.push(ax, ay, az, bx, by, bz, cx, cy, cz);
                    emittedTriangles++;
                    if (ax < bMinX) bMinX = ax; if (ax > bMaxX) bMaxX = ax;
                    if (ay < bMinY) bMinY = ay; if (ay > bMaxY) bMaxY = ay;
                    if (az < bMinZ) bMinZ = az; if (az > bMaxZ) bMaxZ = az;
                    if (bx < bMinX) bMinX = bx; if (bx > bMaxX) bMaxX = bx;
                    if (by < bMinY) bMinY = by; if (by > bMaxY) bMaxY = by;
                    if (bz < bMinZ) bMinZ = bz; if (bz > bMaxZ) bMaxZ = bz;
                    if (cx < bMinX) bMinX = cx; if (cx > bMaxX) bMaxX = cx;
                    if (cy < bMinY) bMinY = cy; if (cy > bMaxY) bMaxY = cy;
                    if (cz < bMinZ) bMinZ = cz; if (cz > bMaxZ) bMaxZ = cz;
                };

                if (indices) {
                    if (indices.length % 3 !== 0) {
                        throw new Error('[MeshCollision.load] Index accessor count must be a multiple of 3');
                    }
                    ensureTriangleBudget(indices.length / 3);
                    for (let k = 0; k < indices.length; k += 3) {
                        emitTri(indices[k], indices[k + 1], indices[k + 2]);
                    }
                } else {
                    if (vertCount % 3 !== 0) {
                        throw new Error('[MeshCollision.load] POSITION accessor count must be a multiple of 3 without indices');
                    }
                    ensureTriangleBudget(vertCount / 3);
                    for (let v = 0; v < vertCount; v += 3) {
                        emitTri(v, v + 1, v + 2);
                    }
                }
            }
        };

        if (meshNodeTransforms.size > 0) {
            for (const [meshIdx, transforms] of meshNodeTransforms) {
                const mesh = meshes[meshIdx];
                if (!mesh) throw new Error(`[MeshCollision.load] Node references missing mesh ${meshIdx}`);
                for (const transform of transforms) {
                    emitMesh(mesh, transform);
                }
            }
        } else {
            for (const mesh of meshes) {
                emitMesh(mesh, IDENTITY_MAT4);
            }
        }

        if (triList.length === 0) {
            throw new Error('[MeshCollision.load] GLB contains no triangles');
        }

        const tris = new Float32Array(triList);
        const bounds: AABB = {
            minX: bMinX, minY: bMinY, minZ: bMinZ,
            maxX: bMaxX, maxY: bMaxY, maxZ: bMaxZ,
        };

        console.log(
            `[MeshCollision.load] OK — ${tris.length / 9} triangles, ` +
            `bounds: [${bMinX.toFixed(2)},${bMinY.toFixed(2)},${bMinZ.toFixed(2)}] → ` +
            `[${bMaxX.toFixed(2)},${bMaxY.toFixed(2)},${bMaxZ.toFixed(2)}]`,
        );
        return new MeshCollision(tris, bounds);
    }

    /** Natural query resolution (world units) — the XZ broadphase cell size — so the reticle
     *  raycast sizes its sweep to the mesh, not to the camera's far clip. */
    queryCellSize(): number {
        return this.cellSize > 0 ? this.cellSize : 0.5;
    }

    /** Stub — no octree metadata for a triangle mesh. Returns synthetic data. */
    getMetadata(): VoxelOctreeMetadata {
        return {
            version: 'mesh-collision/1',
            gridBounds: {
                min: [this.bounds.minX, this.bounds.minY, this.bounds.minZ],
                max: [this.bounds.maxX, this.bounds.maxY, this.bounds.maxZ],
            },
            sceneBounds: {
                min: [this.bounds.minX, this.bounds.minY, this.bounds.minZ],
                max: [this.bounds.maxX, this.bounds.maxY, this.bounds.maxZ],
            },
            voxelResolution: 0,
            leafSize: 0,
            treeDepth: 0,
            numInteriorNodes: 0,
            numMixedLeaves: 0,
            nodeCount: 0,
            leafDataCount: 0,
        };
    }

    /** Stub — debug AABB visualization is voxel-specific; returns an empty array. */
    collectSolidAABBs(_maxCount: number): Float32Array {
        return new Float32Array(0);
    }

    private cellAt(x: number, z: number): number {
        const cx = Math.floor((x - this.gridMinX) / this.cellSize);
        const cz = Math.floor((z - this.gridMinZ) / this.cellSize);
        if (cx < 0 || cx >= this.gridDimX || cz < 0 || cz >= this.gridDimZ) return -1;
        return cz * this.gridDimX + cx;
    }

    /**
     * Cast a downward ray at (x, ?, z) starting from `maxY` (or scene top), return
     * the highest triangle Y intersected. Returns null if no hit.
     */
    findGround(wx: number, wz: number, maxY?: number): number | null {
        if (this.pendingMatrix !== undefined) this.flushPendingTransform();
        const startY = maxY ?? (this.bounds.maxY + 1);
        const cellIdx = this.cellAt(wx, wz);
        if (cellIdx < 0) return null;

        const start = this.cellOffsets[cellIdx];
        const end = this.cellOffsets[cellIdx + 1];

        let bestY: number | null = null;
        for (let i = start; i < end; i++) {
            const triIdx = this.cellTris[i];
            const t = this.rayDownVsTriangle(wx, startY, wz, triIdx);
            if (t !== null && (bestY === null || t > bestY)) {
                bestY = t;
            }
        }
        return bestY;
    }

    /**
     * Cast an upward ray at (x, ?, z) starting from `minY`, return the LOWEST
     * triangle Y intersected (i.e. the floor when looking up from below).
     */
    findGroundInvertedY(wx: number, wz: number, minY: number): number | null {
        if (this.pendingMatrix !== undefined) this.flushPendingTransform();
        const cellIdx = this.cellAt(wx, wz);
        if (cellIdx < 0) return null;

        const start = this.cellOffsets[cellIdx];
        const end = this.cellOffsets[cellIdx + 1];

        let bestY: number | null = null;
        for (let i = start; i < end; i++) {
            const triIdx = this.cellTris[i];
            const t = this.rayUpVsTriangle(wx, minY, wz, triIdx);
            if (t !== null && (bestY === null || t < bestY)) {
                bestY = t;
            }
        }
        return bestY;
    }

    /**
     * World-space helper used by walk mode — searches downward from `worldY +
     * stepHeight` for the nearest ground at (worldX, worldZ).
     */
    findGroundWorld(worldX: number, worldZ: number, worldY: number, stepHeight: number): number | null {
        return this.findGround(worldX, worldZ, worldY + stepHeight);
    }

    /**
     * Coarse "is this point inside or right next to a surface?" test. Mesh
     * collision can't tell solid-volume membership without a closed mesh, so we
     * approximate via "any triangle within a tiny radius of p?".
     */
    checkPointSolid(wx: number, wy: number, wz: number): boolean {
        const r = 0.05;
        return this.checkSphereSolid(wx, wy, wz, r);
    }

    /**
     * True if any triangle within the sphere's bounding cells is within `radius`
     * of the point. Spans every cell the sphere overlaps in XZ — testing only the
     * center cell would miss collisions when the sphere straddles a cell boundary
     * (e.g. fly-mode camera near a wall bucketed into the neighboring cell).
     */
    checkSphereSolid(wx: number, wy: number, wz: number, radius: number): boolean {
        if (this.pendingMatrix !== undefined) this.flushPendingTransform();
        const r2 = radius * radius;
        const cellMinX = Math.max(0, Math.floor((wx - radius - this.gridMinX) / this.cellSize));
        const cellMaxX = Math.min(this.gridDimX - 1, Math.floor((wx + radius - this.gridMinX) / this.cellSize));
        const cellMinZ = Math.max(0, Math.floor((wz - radius - this.gridMinZ) / this.cellSize));
        const cellMaxZ = Math.min(this.gridDimZ - 1, Math.floor((wz + radius - this.gridMinZ) / this.cellSize));
        if (cellMaxX < cellMinX || cellMaxZ < cellMinZ) return false;

        for (let zc = cellMinZ; zc <= cellMaxZ; zc++) {
            for (let xc = cellMinX; xc <= cellMaxX; xc++) {
                const cellIdx = zc * this.gridDimX + xc;
                const start = this.cellOffsets[cellIdx];
                const end = this.cellOffsets[cellIdx + 1];
                for (let i = start; i < end; i++) {
                    const triIdx = this.cellTris[i];
                    const d2 = this.pointTriangleDistanceSq(wx, wy, wz, triIdx);
                    if (d2 <= r2) return true;
                }
            }
        }
        return false;
    }

    /**
     * Precise ray–triangle raycast against the collision mesh (world space). Returns the distance
     * `t` to the NEAREST surface hit along the normalized ray, within `maxDist`, or null on a miss.
     *
     * This is what the cursor reticle / click-to-move / measurement use to land EXACTLY on the
     * surface. Unlike the coarse checkSphereSolid sweep — which "contacts" within ~1 cell (≈1 m) of
     * any triangle and so drops the hit in mid-air when the ray merely skims over the floor — a real
     * ray–triangle test only registers where the ray actually passes THROUGH a triangle, and almost
     * never on small airborne floater triangles. No GPU gsplat pick (that hangs 2.20 WebGPU devices).
     *
     * Brute-force Möller–Trumbore over all triangles (two-sided). Picks are one-shot and infrequent
     * and collision meshes are decimated, so the O(triCount) cost (a few ms) is irrelevant per click.
     */
    raycast(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): number | null {
        if (this.pendingMatrix !== undefined) this.flushPendingTransform();
        const dl = Math.hypot(dx, dy, dz) || 1;
        const rdx = dx / dl, rdy = dy / dl, rdz = dz / dl;
        const tris = this.tris;
        const n = this.triCount;
        const EPS = 1e-7;
        let bestT = maxDist > 0 ? maxDist : Infinity;
        let hit = false;
        for (let i = 0; i < n; i++) {
            const o = i * 9;
            const ax = tris[o], ay = tris[o + 1], az = tris[o + 2];
            const e1x = tris[o + 3] - ax, e1y = tris[o + 4] - ay, e1z = tris[o + 5] - az;
            const e2x = tris[o + 6] - ax, e2y = tris[o + 7] - ay, e2z = tris[o + 8] - az;
            // p = dir × e2
            const px = rdy * e2z - rdz * e2y;
            const py = rdz * e2x - rdx * e2z;
            const pz = rdx * e2y - rdy * e2x;
            const det = e1x * px + e1y * py + e1z * pz;
            if (det > -EPS && det < EPS) continue; // ray parallel to triangle
            const inv = 1 / det;
            const tvx = ox - ax, tvy = oy - ay, tvz = oz - az;
            const u = (tvx * px + tvy * py + tvz * pz) * inv;
            if (u < 0 || u > 1) continue;
            // q = tvec × e1
            const qx = tvy * e1z - tvz * e1y;
            const qy = tvz * e1x - tvx * e1z;
            const qz = tvx * e1y - tvy * e1x;
            const v = (rdx * qx + rdy * qy + rdz * qz) * inv;
            if (v < 0 || u + v > 1) continue;
            const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
            if (t > EPS && t < bestT) { bestT = t; hit = true; }
        }
        return hit ? bestT : null;
    }

    /**
     * Sphere-vs-mesh: find the closest triangle penetration and return a push-out
     * vector that would resolve it. Returns null if no triangle is within radius.
     */
    querySphere(cx: number, cy: number, cz: number, radius: number): PushOut | null {
        if (this.pendingMatrix !== undefined) this.flushPendingTransform();
        // Search across cells whose XZ range can intersect the sphere.
        const cellMinX = Math.max(0, Math.floor((cx - radius - this.gridMinX) / this.cellSize));
        const cellMaxX = Math.min(this.gridDimX - 1, Math.floor((cx + radius - this.gridMinX) / this.cellSize));
        const cellMinZ = Math.max(0, Math.floor((cz - radius - this.gridMinZ) / this.cellSize));
        const cellMaxZ = Math.min(this.gridDimZ - 1, Math.floor((cz + radius - this.gridMinZ) / this.cellSize));

        let bestD2 = radius * radius;
        let bestTri = -1;
        let bestPx = 0, bestPy = 0, bestPz = 0;
        const closest: [number, number, number] = [0, 0, 0];

        for (let zc = cellMinZ; zc <= cellMaxZ; zc++) {
            for (let xc = cellMinX; xc <= cellMaxX; xc++) {
                const cellIdx = zc * this.gridDimX + xc;
                const start = this.cellOffsets[cellIdx];
                const end = this.cellOffsets[cellIdx + 1];
                for (let i = start; i < end; i++) {
                    const triIdx = this.cellTris[i];
                    this.closestPointOnTriangle(cx, cy, cz, triIdx, closest);
                    const dx = cx - closest[0], dy = cy - closest[1], dz = cz - closest[2];
                    const d2 = dx * dx + dy * dy + dz * dz;
                    if (d2 < bestD2) {
                        bestD2 = d2;
                        bestTri = triIdx;
                        bestPx = closest[0]; bestPy = closest[1]; bestPz = closest[2];
                    }
                }
            }
        }
        if (bestTri < 0) return null;

        const dist = Math.sqrt(bestD2);
        if (dist < 1e-6) {
            // Coincident — push along triangle normal (fallback)
            const n: [number, number, number] = [0, 1, 0];
            this.triangleNormal(bestTri, n);
            return {
                x: n[0] * radius,
                y: n[1] * radius,
                z: n[2] * radius,
            };
        }
        const overlap = radius - dist;
        const inv = overlap / dist;
        return {
            x: (cx - bestPx) * inv,
            y: (cy - bestPy) * inv,
            z: (cz - bestPz) * inv,
        };
    }

    // ── Internals ────────────────────────────────────────────────────────

    /** Möller-Trumbore ray-triangle, ray = (x, startY, z) pointing -Y. Returns hit Y or null. */
    private rayDownVsTriangle(rx: number, startY: number, rz: number, triIdx: number): number | null {
        return this.rayVerticalVsTriangle(rx, startY, rz, triIdx, -1);
    }
    private rayUpVsTriangle(rx: number, startY: number, rz: number, triIdx: number): number | null {
        return this.rayVerticalVsTriangle(rx, startY, rz, triIdx, +1);
    }

    private rayVerticalVsTriangle(rx: number, startY: number, rz: number, triIdx: number, dir: 1 | -1): number | null {
        const o = triIdx * 9;
        const ax = this.tris[o],     ay = this.tris[o + 1], az = this.tris[o + 2];
        const bx = this.tris[o + 3], by = this.tris[o + 4], bz = this.tris[o + 5];
        const cx = this.tris[o + 6], cy = this.tris[o + 7], cz_ = this.tris[o + 8];

        // Edges
        const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
        const e2x = cx - ax, e2y = cy - ay, e2z = cz_ - az;
        // Ray dir = (0, dir, 0)
        // h = cross(d, e2) = (dir*e2z, 0, -dir*e2x)
        const hx = dir * e2z, hy = 0, hz = -dir * e2x;
        const a = e1x * hx + e1y * hy + e1z * hz;
        if (a > -1e-7 && a < 1e-7) return null;

        const f = 1 / a;
        const sx = rx - ax, sy = startY - ay, sz = rz - az;
        const u = f * (sx * hx + sy * hy + sz * hz);
        if (u < 0 || u > 1) return null;

        // q = cross(s, e1)
        const qx = sy * e1z - sz * e1y;
        const qy = sz * e1x - sx * e1z;
        const qz = sx * e1y - sy * e1x;
        // v = f * dot(d, q) = f * dir * qy
        const v = f * dir * qy;
        if (v < 0 || u + v > 1) return null;

        // t = f * dot(e2, q)
        const t = f * (e2x * qx + e2y * qy + e2z * qz);
        if (t < 0) return null;

        // Hit Y = startY + dir * t
        return startY + dir * t;
    }

    /** Squared distance from point P to triangle (Ericson, RTCD §5.1.5). */
    private pointTriangleDistanceSq(px: number, py: number, pz: number, triIdx: number): number {
        const c: [number, number, number] = [0, 0, 0];
        this.closestPointOnTriangle(px, py, pz, triIdx, c);
        const dx = px - c[0], dy = py - c[1], dz = pz - c[2];
        return dx * dx + dy * dy + dz * dz;
    }

    /** Caller-supplied `out` is filled with the closest point. No aliasing across calls. */
    private closestPointOnTriangle(px: number, py: number, pz: number, triIdx: number, out: [number, number, number]): void {
        const o = triIdx * 9;
        const ax = this.tris[o],     ay = this.tris[o + 1], az = this.tris[o + 2];
        const bx = this.tris[o + 3], by = this.tris[o + 4], bz = this.tris[o + 5];
        const cx = this.tris[o + 6], cy = this.tris[o + 7], cz_ = this.tris[o + 8];

        // Vectors
        const abx = bx - ax, aby = by - ay, abz = bz - az;
        const acx = cx - ax, acy = cy - ay, acz = cz_ - az;
        const apx = px - ax, apy = py - ay, apz = pz - az;

        const d1 = abx * apx + aby * apy + abz * apz;
        const d2 = acx * apx + acy * apy + acz * apz;
        if (d1 <= 0 && d2 <= 0) {
            out[0] = ax; out[1] = ay; out[2] = az;
            return;
        }

        const bpx = px - bx, bpy = py - by, bpz = pz - bz;
        const d3 = abx * bpx + aby * bpy + abz * bpz;
        const d4 = acx * bpx + acy * bpy + acz * bpz;
        if (d3 >= 0 && d4 <= d3) {
            out[0] = bx; out[1] = by; out[2] = bz;
            return;
        }

        const vc = d1 * d4 - d3 * d2;
        if (vc <= 0 && d1 >= 0 && d3 <= 0) {
            const v = d1 / (d1 - d3);
            out[0] = ax + abx * v;
            out[1] = ay + aby * v;
            out[2] = az + abz * v;
            return;
        }

        const cpx = px - cx, cpy = py - cy, cpz = pz - cz_;
        const d5 = abx * cpx + aby * cpy + abz * cpz;
        const d6 = acx * cpx + acy * cpy + acz * cpz;
        if (d6 >= 0 && d5 <= d6) {
            out[0] = cx; out[1] = cy; out[2] = cz_;
            return;
        }

        const vb = d5 * d2 - d1 * d6;
        if (vb <= 0 && d2 >= 0 && d6 <= 0) {
            const w = d2 / (d2 - d6);
            out[0] = ax + acx * w;
            out[1] = ay + acy * w;
            out[2] = az + acz * w;
            return;
        }

        const va = d3 * d6 - d5 * d4;
        const denom4 = (d4 - d3) + (d5 - d6);
        if (va <= 0 && (d4 - d3) >= 0 && (d5 - d6) >= 0 && denom4 > 0) {
            const w = (d4 - d3) / denom4;
            out[0] = bx + (cx - bx) * w;
            out[1] = by + (cy - by) * w;
            out[2] = bz + (cz_ - bz) * w;
            return;
        }

        const denom = 1 / (va + vb + vc);
        const v = vb * denom;
        const w = vc * denom;
        out[0] = ax + abx * v + acx * w;
        out[1] = ay + aby * v + acy * w;
        out[2] = az + abz * v + acz * w;
    }

    /** Caller-supplied `out` is filled with the unit normal. No aliasing across calls. */
    private triangleNormal(triIdx: number, out: [number, number, number]): void {
        const o = triIdx * 9;
        const ax = this.tris[o],     ay = this.tris[o + 1], az = this.tris[o + 2];
        const bx = this.tris[o + 3], by = this.tris[o + 4], bz = this.tris[o + 5];
        const cx = this.tris[o + 6], cy = this.tris[o + 7], cz_ = this.tris[o + 8];
        const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
        const e2x = cx - ax, e2y = cy - ay, e2z = cz_ - az;
        let nx = e1y * e2z - e1z * e2y;
        let ny = e1z * e2x - e1x * e2z;
        let nz = e1x * e2y - e1y * e2x;
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (len > 1e-7) { nx /= len; ny /= len; nz /= len; }
        out[0] = nx; out[1] = ny; out[2] = nz;
    }
}
